import React from 'react';
import BackButton from '@material-ui/icons/ArrowBack';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
	Button,
	ExpansionPanel,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Typography,
	withStyles,
	withTheme,
} from '@material-ui/core';
import cloneDeep from 'lodash.clonedeep';
import IsEmpty from 'lodash.isempty';
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import BreadcrumbsContainer from 'features/Breadcrumbs/containers/BreadcrumbsContainer';
import buildQuantityList from 'shared/utils/dropDownHelpers';
import config from 'shared/config';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import history from 'redux/history';
import ProductClass from 'models/Product';
import { DEPTH, HEIGHT, WIDTH } from 'models/constants';
import {
	Description,
	OnDisplay,
	ProductAttributes,
	ProductDimensions,
	ProductReviews,
	Rating,
	StockQuantity,
	Sku,
	VendorName,
	Softkit,
} from 'features/ProductDetails';
import { formatCurrency, startCase } from 'shared/utils/htmlHelpers';
import { getStoreLocation, getStoreName } from 'shared/utils/storeLocationHelpers';
import { SHOPPING_CART, WISH_LIST } from 'redux/domain/Cart/constants';
import ProductSlider from './ProductSlider';
import RelatedProducts from './RelatedProducts';
import styles from './styles.css';

const stylesMUI = theme => ({
	selectQuantity: {
		bottom: '0.3em',
		margin: '0.9em 0 1.1em 3px',
	},

	productPaperPadding: {
		padding: '6px 12px',
	},

	productSpacer: {
		padding: '4px 0',
	},

	primaryHeader: {
		margin: '0',
		/* Adjusting line-height so heading text is not smushed on wrap */
		lineHeight: '36px',
	},

	productReviews: {
		display: 'flex',
	},

	starRating: {
		marginRight: '0.5em',
	},

	productPrice: {
		fontSize: '2em',
		fontWeight: 'bold',
		padding: '0.5em 0',
	},

	productPriceReviewsVendorOff: {
		fontSize: '2em',
		fontWeight: 'normal',
		padding: '0.5em 0',
	},

	prodMsrp: {
		display: 'initial',
		textDecoration: 'line-through',
		fontSize: '0.5em',
		paddingLeft: '8px',
	},

	selectInput: {
		color: theme.palette.text.primary,
	},

	styleIconButton: {
		right: '6px',
		top: '5px',
		backgroundColor: 'transparent',
		color: 'inherit',
	},

	styleBackButton: {
		height: '35px',
		width: '35px',
	},
});

class ProductFamilyFull extends React.Component {
	constructor(props) {
		super();

		this.state = {
			attributeValues: [],
			configuredProduct: props.product,
			selectedMemberIndex: 0,
			selectedValues: [],
			quantity: 1,
		};

		this.handleAttributeValueChange = this.handleAttributeValueChange.bind(this);
		this.handleComponentChange = this.handleComponentChange.bind(this);
	}

	UNSAFE_componentWillMount() {
		// set the default product only if the product info for the product family is retrieved
		if (this.props.product.family.members !== undefined) {
			this.setAttributeValues(this.props.product);
		}
	}

	componentDidMount() {
		const { product: { family: { products: [initialProduct] } } } = this.props;
		if (initialProduct.store !== getStoreLocation()) {
			this.props.getProductFamilyOnDisplay(initialProduct.id);
		}
		this.props.getProductFamilyQuantity(initialProduct.id);
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.product.productId !== this.state.configuredProduct.productId) {
			// we retrieved the family product information so set the first product as the default
			this.setState({
				configuredProduct: nextProps.product,
				selectedMemberIndex: 0,
			});
		}

		if (
			this.state.attributeValues.length === 0 &&
			this.props.product.family.members !== undefined
		) {
			this.setAttributeValues(nextProps.product);
		}
	}

	/**
	 * Build the attribute values and initialize the selected values
	 * @param {product} product
	 */
	setAttributeValues(product) {
		// default to the first family member
		const initialSelectedValues = product.family.members[0].attributeValues;
		// set attribute values. key is the identifier and values is the set of options
		const populatedAttributeValues = product.family.attributes.map(value => {
			const values = value.attributeValues.map(attribute => attribute.attributeValueName);
			return {
				key: value.textPrompt,
				values,
			};
		});

		this.setState({
			selectedValues: initialSelectedValues,
			attributeValues: populatedAttributeValues,
		});
	}

	/**
	 * Build an array of images if the image only has a single default image.
	 * @param {object} product
	 */
	getProductImages = product => {
		// Todo replace with lodash.
		if (product.pictureUrls === undefined) {
			return null;
		}

		// ProductSlider wants the pictureUrls in this format.
		return product.pictureUrls.length > 0
			? product.pictureUrls
			: [
					{
						full: product.pictureUrl,
						thumb: product.pictureUrl,
					},
				];
	};

	/**
	 * return the value for the attribute from the state's selected values
	 * @param {key} key for the attribute
	 */
	getSelectedValue(key) {
		const selectedValue = this.state.selectedValues.find(element => element.Key === key);
		return selectedValue.Value;
	}

	getProductFamilyAverageReview = productReviews => {
		let averageProdFamReviews = 0;
		let count = 0;
		productReviews.forEach(review => {
			averageProdFamReviews += review.Rating;
			count += 1;
		});
		return averageProdFamReviews / count;
	};

	/**
	 * User has selected a new attribute value.
	 * @param {key} key the selected attribute key
	 * @param {value} value the selected value
	 */
	handleAttributeValueChange(key, value) {
		// maintain immutability and find the value to change
		const selectedValues = this.state.selectedValues.map(element => {
			if (element.Key === key) {
				return Object.assign({}, element, { Value: value });
			}
			return Object.assign({}, element);
		});

		const bestMatch = this.props.product.family.findProduct(key, selectedValues);
		// updated configuredProduct
		const updatedProduct = cloneDeep(this.state.configuredProduct);

		const selectedMember = this.props.product.family.products[this.state.selectedMemberIndex];
		// add new product
		updatedProduct.addSelectedComponent(bestMatch.product.sku);
		// remove old product
		updatedProduct.removeSelectedComponent(selectedMember.sku);

		// update state
		this.setState({
			selectedMemberIndex: bestMatch.index,
			selectedValues: bestMatch.selectedValues,
			configuredProduct: updatedProduct,
		});

		// update on Display if the store changed
		if (bestMatch.product.store !== getStoreLocation()) {
			this.props.getProductFamilyOnDisplay(bestMatch.product.productId);
		}

		// check stock quantity
		this.props.getProductFamilyQuantity(bestMatch.product.productId);
	}

	handleComponentChange(prevSelection, currentSelection) {
		// This is only called when a soft kit is a product family member
		const product = cloneDeep(this.state.configuredProduct);
		product.addSelectedComponent(currentSelection);
		product.removeSelectedComponent(prevSelection);
		this.setState({
			configuredProduct: product,
		});
	}

	// Display a product as an overlay
	render() {
		const { theme } = this.props;
		const { addItemToCart, classes } = this.props;
		const categoryId = this.props.categoryId === 'search' ? '' : this.props.categoryId;
		const productFamily = this.props.product;
		productFamily.parentCategoryId =
			categoryId === '' ? productFamily.parentCategoryId : parseInt(categoryId, 10);

		const productReviews = productFamily.productReviews.Items;
		productFamily.approvedTotalReviews = productReviews.length;
		if (!IsEmpty(productReviews)) {
			productFamily.averageReview = this.getProductFamilyAverageReview(productReviews);
		}

		const { ShowCurrentPrice, ShowMsrp, ShowReviews, ShowVendor } = config.PRODUCT;
		const selectedMember = productFamily.family.products
			? productFamily.family.products[this.state.selectedMemberIndex]
			: undefined;
		const product = selectedMember
			? new ProductClass(selectedMember.id, selectedMember)
			: new ProductClass(productFamily.productId, productFamily);
		const { attributeValues } = this.state;

		// Display currentPrice as main price if ShowCurrentPrice is true AND currentPrice is not 0/null/undefined/NaN
		// Display MSRP as main price if currentPrice is not used AND ShowMsrp is true AND MSRP is not 0/null/undefined/NaN
		// In all other cases, display nothing.
		const mainPrice = (ShowCurrentPrice && product.currentPrice) || (ShowMsrp && product.msrp);
		const formatPrice = formatCurrency(mainPrice);

		// Display scratched out price if:
		// msrp exists (not undefined/null/NaN) and is not 0
		// msrp is greater than (and not equal to) mainPrice
		// ShowCurrentPrice and ShowMsrp are both true
		const formatSecondaryPrice =
			// eslint-disable-next-line no-nested-ternary
			ShowMsrp && product.msrp
				? product.msrp > mainPrice && formatCurrency(product.msrp)
				: ShowCurrentPrice && product.oldPrice
					? product.oldPrice > mainPrice && formatCurrency(product.oldPrice)
					: null;

		// get product dimensions
		const width = product.findSpecAttributes(WIDTH);
		const height = product.findSpecAttributes(HEIGHT);
		const depth = product.findSpecAttributes(DEPTH);

		// get selected member attributes (not including dimensions or empty attributes)

		const attributes = product.specAttributes.filter(
			attribute =>
				attribute.name !== 'Height' &&
				attribute.name !== 'Width' &&
				attribute.name !== 'Depth' &&
				attribute.value !== '',
		);

		return (
			<div className={styles['product-full-container']}>
				<div className={styles['product-navigation']}>
					<IconButton className={classes.styleIconButton}>
						<BackButton
							onClick={history.back}
							className={classes.styleBackButton}
							color="inherit"
						/>
					</IconButton>
					<div className={styles['product-breadcrumbs']}>
						<BreadcrumbsContainer product={productFamily} productCatId={categoryId} />
					</div>
				</div>
				<div className={styles['product-full-column1']}>
					<div className={styles['product-full-col-margins']}>
						<ErrorBoundary>
							<div className={styles['product-modal-slider']}>
								<ProductSlider
									pictureUrls={this.getProductImages(product)}
									key={product.productId}
								/>
							</div>
						</ErrorBoundary>
					</div>
				</div>
				<div className={styles['product-full-column2']}>
					<div className={styles['product-full-col-margins']}>
						<Paper elevation={1} className={classes.productPaperPadding}>
							<ErrorBoundary>
								<div className={classes.productSpacer}>
									<h1 className={classes.primaryHeader}>{productFamily.name}</h1>
								</div>
								{ShowVendor ? <VendorName vendorName={product.vendorName} /> : null}
								{ShowReviews ? (
									<div className={classes.productSpacer}>
										<div className={classes.productReviews}>
											<Rating productReviewRating={productFamily.averageReview} />
											<div>
												{productFamily.approvedTotalReviews}
												{productFamily.approvedTotalReviews === 1 ? ' Review' : ' Reviews'}
											</div>
										</div>
									</div>
								) : null}
								{mainPrice ? (
									<div
										className={
											!ShowVendor && !ShowReviews
												? classes.productPriceReviewsVendorOff
												: classes.productPrice
										}
										style={
											product.oldPrice && product.oldPrice > product.currentPrice
												? { color: config.COLORS.ProductDetailSalePrice }
												: { color: theme.palette.text.productPrice }
										}
									>
										{formatPrice}
										{formatSecondaryPrice ? (
											<div
												className={classes.prodMsrp}
												style={{ color: theme.palette.text.secondary }}
											>
												{formatSecondaryPrice}
											</div>
										) : null}
									</div>
								) : null}
								{config.PRODUCT.ShowInStock ? (
									<StockQuantity productStockQuantity={product.stockQuantity} />
								) : null}
								{config.PRODUCT.ShowOnDisplay ? (
									<OnDisplay
										onDisplay={product.onDisplay}
										productStoreName={getStoreName(product.store)}
									/>
								) : null}

								{config.GENERAL.ShowCart ? (
									<div>
										<div className={classes.productSpacer}>
											<b>Quantity</b>
											<br />
											<Select
												value={this.state.quantity}
												onChange={event => this.setState({ quantity: event.target.value })}
												className={classes.selectQuantity}
												MenuProps={{
													transformOrigin: { vertical: 'top', horizontal: 'left' },
													getContentAnchorEl: null,
												}}
												inputProps={{ className: classes.selectInput }}
											>
												{buildQuantityList()}
											</Select>
										</div>
										<div className={classes.productSpacer}>
											<Button
												variant="contained"
												style={{
													backgroundColor: theme.palette.secondary.main,
													color: theme.palette.getContrastText(theme.palette.secondary.main),
												}}
												onClick={() =>
													addItemToCart(
														this.state.configuredProduct,
														SHOPPING_CART,
														this.state.quantity,
													)
												}
											>
												Add To Cart
											</Button>
										</div>

										<div className={classes.productSpacer}>
											<Button
												variant="contained"
												onClick={() =>
													addItemToCart(
														this.state.configuredProduct,
														WISH_LIST,
														this.state.quantity,
													)
												}
												color="inherit"
												style={{ backgroundColor: theme.palette.background.paper }}
											>
												Save To Idea Board
											</Button>
										</div>
									</div>
								) : null}
							</ErrorBoundary>
						</Paper>
						<ErrorBoundary>
							{attributeValues.length > 0 ? (
								<div className={styles['product-expandable-spacer']}>
									<Accordion defaultExpanded>
										<AccordionSummary expandIcon={<ExpandMoreIcon />}>
											<Typography variant="subtitle2">Product Options</Typography>
										</AccordionSummary>
										<AccordionDetails>
											<div className={styles['options-container']}>
												{attributeValues.map(attribute => (
													// need a unique key prop for each child in array or iterator
													<div key={`${attribute.key} div`}>
														<b className={styles['attribute-label']}>{startCase(attribute.key)}</b>
														<Select
															key={attribute.key}
															value={this.getSelectedValue(attribute.key)}
															onChange={event => {
																this.handleAttributeValueChange(attribute.key, event.target.value);
															}}
															className={classes.selectQuantity}
															MenuProps={{
																transformOrigin: { vertical: 'top', horizontal: 'left' },
																getContentAnchorEl: null,
															}}
															inputProps={{ className: classes.selectInput }}
														>
															{attribute.values.map(currValue => (
																<MenuItem key={currValue} value={currValue}>
																	{startCase(currValue)}
																</MenuItem>
															))}
														</Select>
													</div>
												))}
											</div>
										</AccordionDetails>
									</Accordion>
								</div>
							) : null}
						</ErrorBoundary>
						<Softkit product={product} handleSelection={this.handleComponentChange} />
						<Description shortDescription={product.shortDescription} />
						<Description fullDescription={product.fullDescription} />
						{ShowReviews && productFamily.approvedTotalReviews > 0 && !IsEmpty(productReviews) ? (
							<ProductReviews productReviews={productReviews} />
						) : null}
						<ErrorBoundary>
							{IsEmpty(product.addonProducts) ? null : (
								<div className={styles['addon-items-container']}>
									<RelatedProducts
										relatedProducts={product.addonProducts}
										title={config.ProductAddOnLabel}
									/>
								</div>
							)}
						</ErrorBoundary>
						<ProductDimensions width={width} height={height} depth={depth} />
						<ErrorBoundary>
							{IsEmpty(product.relatedProducts) ? null : (
								<div className={styles['related-items-container']}>
									<RelatedProducts relatedProducts={product.relatedProducts} />
								</div>
							)}
						</ErrorBoundary>
						{!IsEmpty(attributes) ? (
							<ProductAttributes attributes={attributes} productId={product.productId} />
						) : null}
						<Sku
							isSoftkit={product.isSoftkit}
							selectedComponents={product.selectedComponents}
							sku={product.sku}
						/>
						{/* Spacer to make space between the last card and the bottom of the page */}
						<div className={styles['bottom-spacer']} />
					</div>
				</div>
			</div>
		);
	}
}

ProductFamilyFull.propTypes = {
	addItemToCart: PropTypes.func.isRequired,
	categoryId: PropTypes.string,
	classes: PropTypes.shape({
		selectQuantity: PropTypes.string,
		productPaperPadding: PropTypes.string,
		productSpacer: PropTypes.string,
		primaryHeader: PropTypes.string,
		productReviews: PropTypes.string,
		productPrice: PropTypes.string,
		prodMsrp: PropTypes.string,
		selectInput: PropTypes.string,
		starRating: PropTypes.string,
	}).isRequired,
	getProductFamilyOnDisplay: PropTypes.func.isRequired,
	getProductFamilyQuantity: PropTypes.func.isRequired,
	product: PropTypes.instanceOf(ProductClass).isRequired,
	theme: PropTypes.instanceOf(Object).isRequired,
};

ProductFamilyFull.defaultProps = {
	categoryId: '',
};
// for testing purposes
export { ProductFamilyFull as ProductFamilyFullClass };

export default compose(withStyles(stylesMUI, { name: 'ProductFamilyFull' }), withTheme)(
	ProductFamilyFull,
);
